<template>
  <UIModal
    :model-value="modelValue"
    size="m"
    content-class="search-list-modal"
    @update:model-value="emit('update:model-value', $event)"
  >
    <div class="search-list-modal__title">
      {{ userType === 'followers' ? $t('components.business.usersModal.followers') : $t('components.business.usersModal.followings') }}
    </div>

    <div class="search-list-modal__content">
      <FormTextField
        v-model="searchValue"
        :name="userType === 'followers' ? 'followers' : 'followings'"
        color="grey"
        :placeholder="$t('components.business.usersModal.search')"
        icon-left="search-icon"
        class="search-list-modal__search"
      />

      <UiLoader
        v-if="loading"
        class="search-list-modal__loader"
      />

      <div
        v-else-if="data?.items?.length"
        ref="$modal"
        class="search-list-modal__users"
      >
        <UiUser
          v-for="(user, index) in data.items"
          :key="index"
          :user="user"
          size="l"
          show-follow
          class="search-list-modal__user"
          @change-follow="debouncedFollowUpdate"
        />

        <div
          v-if="loadMore"
          class="search-list-modal__bottom"
        >
          <UiLoader
            class="search-list-modal__loader-small"
            small
          />
        </div>
      </div>

      <div
        v-else
        class="search-list-modal__no-elements"
      >
        {{ $t('components.business.usersModal.notFound') }}
      </div>
    </div>
  </UIModal>
</template>

<script lang="ts" setup>
import { PageOfUserDTO } from '~/restAPI/data-contracts';
import { useApi } from '~/restAPI';
import { Publicprofile } from '~/restAPI/Publicprofile';
import { isAbortError } from '~/utils/api';

const cancelGetFollowers = 'cancelGetFollowers';
const { createApiInstance } = useApi();
const publicProfileApi = createApiInstance(Publicprofile);

const props = defineProps<{
  modelValue: boolean
  userId: number,
  userType: 'followers' | 'followings'
}>();

const emit = defineEmits<{
  (e: 'update:model-value', value: boolean): void,
  (e: 'changeFollow'): void,
}>();

const $modal = ref();
const currentPage = ref(1);
const loading = ref(false);
const loadMore = ref(false);
const data = ref<PageOfUserDTO>(getDefaultData());
const searchValue = ref('');

const methodName = computed(() => {
  if (props.userType === 'followers') return 'getFollowersUsingGet1';

  return 'getFollowingsUsingGet1';
});

useInfiniteScroll(
  $modal,
  async () => {
    if (data.value?.totalPages === 1 || currentPage.value >= (data.value.totalPages ?? 0)) return;

    currentPage.value += 1;
    loadMore.value = true;

    await getData();

    // TODO after vueuse 10.* there is a bug
    $modal.value.scrollBy(0, 1);
  },
  { distance: 10, interval: 500 },
);

function getDefaultData() {
  return {
    items: [],
    itemsCount: 0,
    totalAmount: 0,
    totalPages: 0,
  };
}

async function getData() {
  if (loading.value) {
    publicProfileApi.abortRequest(cancelGetFollowers);
  }

  if (!loadMore.value) {
    currentPage.value = 1;
    loading.value = true;
  }

  try {
    const followersData = await publicProfileApi[methodName.value]({
      userId: props.userId,
      page: currentPage.value,
      pageSize: 20,
      query: searchValue.value,
    }, {
      format: 'json',
      cancelToken: cancelGetFollowers,
    }).then((res) => res?.data?.data);

    if (loadMore.value) {
      data.value.items?.push(...(followersData?.items ?? []));
    } else {
      data.value = followersData ?? getDefaultData();
    }

    loading.value = false;
  } catch (error) {
    if (!isAbortError(error)) {
      loading.value = false;
    } else {
      console.error(error);
    }
  } finally {
    loadMore.value = false;
  }
}

const debouncedFollowUpdate = useDebounceFn(() => {
  emit('changeFollow');
}, 1500);

watch(() => props.modelValue, (value) => {
  if (!value) {
    data.value = getDefaultData();
    searchValue.value = '';
    return;
  }

  getData();
});

watchDebounced(() => searchValue.value, () => {
  getData();
}, { debounce: 300 });
</script>

<style lang="scss">
.search-list-modal {
  padding-top: 12px !important;
  padding-bottom: 24px !important;
  display: flex;
  flex-direction: column;

  .modal-close {
    top: 12px !important;
  }

  &__title {
    font-size: 16px;
    text-align: center;
    margin: 0 -40px;
    padding-bottom: 12px;
    padding-left: 40px;
    border-bottom: 1px solid #f2f2f2;

    @media screen and (min-width: 767px) {
      font-size: 22px;
      text-align: left;
    }
  }

  &__content {
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1 1 auto;
  }

  &__search {
    margin-bottom: 16px;
  }

  &__loader {
    margin: auto;

    @media screen and (min-width: 767px) {
      margin: 16px auto;
    }
  }

  &__loader-small {
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
  }

  &__bottom {
    height: 44px;
    position: relative;
  }

  &__no-elements {
    font-size: 14px;
  }

  &__users {
    overflow-y: auto;

    @media screen and (min-width: 767px) {
      max-height: 300px;
    }
  }

  &__user {
    & + & {
      margin-top: 16px;
    }
  }
}
</style>
